/**
 * Redux slice for the Subscription Plans feature.
 *
 * Holds the customer's list of subscriptions, whichever one is open on screen,
 * and which of the three sections is expanded.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StatusType } from '../../types';
import {
  CancellationStatus,
  Subscription,
  SubscriptionPaymentMethod,
  SubscriptionSection,
  SubscriptionStatus,
  SubscriptionTransaction,
} from '../../types/subscription.type';
import {
  cancelSubscription,
  getSubscriptionDetails,
  getSubscriptionPaymentSources,
  getSubscriptionTransactions,
  getSubscriptions,
} from '../actions/subscription.action';

interface SubscriptionState {
  /** Every subscription the customer has. Shown in the sidebar's Subscriptions list. */
  subscriptions: Subscription[];
  /** The subscription the main page is showing. */
  selectedSubscription: Subscription | null;
  /** Which section is open. `null` means all three are collapsed. */
  openSection: SubscriptionSection | null;
  status: StatusType;
  error: string | null;

  /* The two lists the plan used to carry, each fetched on its own and tracked
     on its own so a slow history does not blank the page, and adding a card
     does not re-read the plan. */
  transactions: SubscriptionTransaction[];
  transactionsTotal: number;
  transactionsHasMore: boolean;
  transactionsStatus: StatusType;

  paymentSources: SubscriptionPaymentMethod[];
  paymentSourcesStatus: StatusType;
}

const initialState: SubscriptionState = {
  subscriptions: [],
  selectedSubscription: null,
  openSection: null,
  status: StatusType.Idle,
  error: null,

  transactions: [],
  transactionsTotal: 0,
  transactionsHasMore: false,
  transactionsStatus: StatusType.Idle,

  paymentSources: [],
  paymentSourcesStatus: StatusType.Idle,
};

const subscriptionReducer = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    /** Opens a section, or closes it if it was already open. */
    toggleSection: (state, action: PayloadAction<SubscriptionSection>) => {
      state.openSection =
        state.openSection === action.payload ? null : action.payload;
    },
    /** Collapses every section. Used when switching plans. */
    closeAllSections: (state) => {
      state.openSection = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- list ----
      .addCase(getSubscriptions.pending, (state) => {
        state.status = StatusType.Loading;
        state.error = null;
      })
      .addCase(
        getSubscriptions.fulfilled,
        (state, action: PayloadAction<Subscription[]>) => {
          state.status = StatusType.Succeeded;
          state.subscriptions = action.payload;
        }
      )
      .addCase(getSubscriptions.rejected, (state, action) => {
        state.status = StatusType.Failed;
        state.error = action.payload || 'Unable to load subscriptions.';
      })

      // ---- one subscription ----
      .addCase(getSubscriptionDetails.pending, (state) => {
        state.status = StatusType.Loading;
        state.error = null;
      })
      .addCase(
        getSubscriptionDetails.fulfilled,
        (state, action: PayloadAction<Subscription>) => {
          state.status = StatusType.Succeeded;
          state.selectedSubscription = action.payload;
        }
      )
      .addCase(getSubscriptionDetails.rejected, (state, action) => {
        state.status = StatusType.Failed;
        state.error = action.payload || 'Unable to load this subscription.';
      })

      // ---- charge history ----
      .addCase(getSubscriptionTransactions.pending, (state) => {
        state.transactionsStatus = StatusType.Loading;
      })
      .addCase(getSubscriptionTransactions.fulfilled, (state, action) => {
        state.transactionsStatus = StatusType.Succeeded;

        const { transactions, total, offset, has_more } = action.payload;

        // A page after the first is appended; the first replaces what was there,
        // so switching plans never shows the previous plan's charges.
        state.transactions = offset > 0 ? [...state.transactions, ...transactions] : transactions;
        state.transactionsTotal = total;
        state.transactionsHasMore = has_more;
      })
      .addCase(getSubscriptionTransactions.rejected, (state) => {
        state.transactionsStatus = StatusType.Failed;
      })

      // ---- saved cards ----
      .addCase(getSubscriptionPaymentSources.pending, (state) => {
        state.paymentSourcesStatus = StatusType.Loading;
      })
      .addCase(
        getSubscriptionPaymentSources.fulfilled,
        (state, action: PayloadAction<SubscriptionPaymentMethod[]>) => {
          state.paymentSourcesStatus = StatusType.Succeeded;
          state.paymentSources = action.payload;
        }
      )
      .addCase(getSubscriptionPaymentSources.rejected, (state) => {
        state.paymentSourcesStatus = StatusType.Failed;
      })

      // ---- cancellation ----
      .addCase(cancelSubscription.pending, (state) => {
        state.status = StatusType.Loading;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.status = StatusType.Succeeded;

        const { subscription_id, cancellation_status } = action.payload;

        // An approved cancellation ends the plan. A pending one leaves it
        // active until the business decides.
        const nextStatus =
          cancellation_status === CancellationStatus.Approved
            ? SubscriptionStatus.Cancelled
            : SubscriptionStatus.Active;

        if (state.selectedSubscription?.subscription_id === subscription_id) {
          state.selectedSubscription.cancellation_status = cancellation_status;
          state.selectedSubscription.status = nextStatus;
        }

        state.subscriptions = state.subscriptions.map((item) =>
          item.subscription_id === subscription_id
            ? { ...item, cancellation_status, status: nextStatus }
            : item
        );
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.status = StatusType.Failed;
        state.error = action.payload || 'Unable to cancel this subscription.';
      });
  },
});

export const { toggleSection, closeAllSections } = subscriptionReducer.actions;
export default subscriptionReducer.reducer;
